import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import {
  BarChart3,
  Activity,
  CheckCircle2,
  AlertTriangle,
  Timer,
  RefreshCw,
  Copy,
  Check,
  Search,
  Gauge,
} from 'lucide-react';
import type { Application } from '../types';
import { alert } from '../utils/toast';

interface ContextType {
  application: Application;
}

interface AnalyticsData {
  application: {
    id: string;
    name: string;
    sandbox_mode: boolean;
    status: string;
  };
  period: string;
  summary: {
    total_requests: number;
    successful_requests: number;
    client_errors: number;
    server_errors: number;
    average_response_time: number;
    total_cost: number;
    success_rate: number;
    error_rate: number;
  };
  subscriptions_quota: Array<{
    subscription_id: string;
    product_name: string;
    tier: string;
    monthly_quota: number;
    current_month_usage: number;
    remaining_quota: number;
    quota_percentage: number;
    rate_limit_per_minute: number;
    monthly_price: number;
  }>;
  timeline: Array<{
    timestamp: string;
    total: number;
    success: number;
    errors: number;
    avg_latency: number;
  }>;
  top_endpoints: Array<{
    endpoint: string;
    method: string;
    count: number;
    avg_latency: number;
    error_rate: number;
  }>;
  recent_requests: Array<{
    request_id: string;
    endpoint: string;
    http_method: string;
    status_code: number;
    response_time_ms: number;
    created_at: string;
  }>;
}

export const ApplicationAnalytics: React.FC = () => {
  const { application } = useOutletContext<ContextType>();
  const { client } = useAuthStore();
  const [period, setPeriod] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: analyticsData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery<AnalyticsData>({
    queryKey: ['application-analytics', client?.id, application.id, period],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: AnalyticsData }>(
        `/clients/${client?.id}/applications/${application.id}/analytics`,
        {
          params: { period },
        }
      );
      return response.data.data;
    },
    enabled: !!client?.id && !!application.id,
    refetchInterval: 30000, // auto-refresh every 30s
  });

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    alert.success('ID da requisição copiado!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const summary = analyticsData?.summary || {
    total_requests: 0,
    successful_requests: 0,
    client_errors: 0,
    server_errors: 0,
    average_response_time: 0,
    total_cost: 0,
    success_rate: 100,
    error_rate: 0,
  };

  const quotas = analyticsData?.subscriptions_quota || [];
  const topEndpoints = analyticsData?.top_endpoints || [];
  const recentRequests = analyticsData?.recent_requests || [];

  const filteredRequests = recentRequests.filter((r) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      r.endpoint.toLowerCase().includes(q) ||
      r.http_method.toLowerCase().includes(q) ||
      r.request_id.toLowerCase().includes(q) ||
      r.status_code.toString().includes(q)
    );
  });

  const getStatusColor = (code: number) => {
    if (code >= 200 && code < 300) return 'badge-success';
    if (code >= 300 && code < 400) return 'badge-info';
    if (code >= 400 && code < 500) return 'badge-warning';
    return 'badge-danger';
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <BarChart3 className="w-6 h-6 text-primary-500" />
            <span>Métricas de Consumo & Desempenho</span>
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Acompanhe o consumo da quota mensal, latência e diagnóstico de chamadas da aplicação{' '}
            <strong className="text-slate-800 dark:text-slate-200">{application.name}</strong>.
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200 dark:border-slate-700">
            {(['24h', '7d', '30d', '90d'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  period === p
                    ? 'bg-white dark:bg-slate-900 text-primary-600 dark:text-primary-400 shadow-xs'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {p === '24h'
                  ? 'Últimas 24h'
                  : p === '7d'
                    ? '7 dias'
                    : p === '30d'
                      ? '30 dias'
                      : '90 dias'}
              </button>
            ))}
          </div>

          <button
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
            className="btn-base btn-sm btn-secondary p-2"
            title="Atualizar Métricas"
          >
            <RefreshCw
              className={`w-4 h-4 text-slate-600 dark:text-slate-300 ${
                isRefetching ? 'animate-spin' : ''
              }`}
            />
          </button>
        </div>
      </div>

      {/* Quota Progress Gauges */}
      {quotas.length > 0 && (
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Gauge className="w-5 h-5 text-primary-500" />
              <span>Consumo da Quota Mensal Contratada</span>
            </h3>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Ciclo Mensal: {new Date().toLocaleString('pt-PT', { month: 'long', year: 'numeric' })}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quotas.map((q) => {
              const isWarning = q.quota_percentage >= 80 && q.quota_percentage < 95;
              const isExceeded = q.quota_percentage >= 95;
              const barColor = isExceeded
                ? 'bg-rose-500'
                : isWarning
                  ? 'bg-amber-500'
                  : 'bg-emerald-500';

              return (
                <div
                  key={q.subscription_id}
                  className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-200/70 dark:border-slate-700/80 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-slate-900 dark:text-white text-sm">
                        {q.product_name}
                      </span>
                      <span className="badge badge-info uppercase ml-2 text-[10px]">
                        Plano {q.tier}
                      </span>
                    </div>
                    <span
                      className={`text-xs font-bold ${
                        isExceeded
                          ? 'text-rose-600 dark:text-rose-400'
                          : isWarning
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      {q.quota_percentage}% Usado
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-500 ${barColor}`}
                      style={{ width: `${Math.min(100, q.quota_percentage)}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                    <span>
                      Consumo:{' '}
                      <strong className="text-slate-800 dark:text-slate-200">
                        {q.current_month_usage.toLocaleString('pt-PT')}
                      </strong>{' '}
                      / {q.monthly_quota.toLocaleString('pt-PT')} req
                    </span>
                    <span>
                      Restantes:{' '}
                      <strong className="text-slate-800 dark:text-slate-200">
                        {q.remaining_quota.toLocaleString('pt-PT')}
                      </strong>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Requisições */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Total de Chamadas
            </p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {summary.total_requests.toLocaleString('pt-PT')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Período selecionado ({period})
            </p>
          </div>
          <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400">
            <Activity className="w-6 h-6" />
          </div>
        </div>

        {/* Taxa de Sucesso */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Taxa de Sucesso
            </p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {summary.success_rate}%
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {summary.successful_requests.toLocaleString('pt-PT')} requisições 2xx
            </p>
          </div>
          <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-6 h-6" />
          </div>
        </div>

        {/* Latência Média */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Latência Média
            </p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
              {summary.average_response_time} ms
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Tempo de resposta Kong</p>
          </div>
          <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400">
            <Timer className="w-6 h-6" />
          </div>
        </div>

        {/* Erros */}
        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Erros Registados
            </p>
            <p className="text-2xl font-bold text-rose-600 dark:text-rose-400 mt-1">
              {(summary.client_errors + summary.server_errors).toLocaleString('pt-PT')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {summary.client_errors} (4xx) • {summary.server_errors} (5xx)
            </p>
          </div>
          <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400">
            <AlertTriangle className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Top Endpoints Table */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 dark:border-slate-800">
          <h3 className="font-bold text-slate-900 dark:text-white text-base">
            Rotas Mais Solicitadas
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Distribuição de tráfego por endpoint consumido através do gateway
          </p>
        </div>

        {topEndpoints.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
            Ainda não foram registadas chamadas a rotas neste período.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Método</th>
                  <th>Endpoint</th>
                  <th>Volume de Chamadas</th>
                  <th>Latência Média</th>
                  <th>Taxa de Erro</th>
                </tr>
              </thead>
              <tbody>
                {topEndpoints.map((ep, idx) => (
                  <tr key={idx}>
                    <td>
                      <span className="badge badge-info uppercase font-mono font-bold text-[11px]">
                        {ep.method}
                      </span>
                    </td>
                    <td className="font-mono text-xs font-semibold text-slate-900 dark:text-white">
                      {ep.endpoint}
                    </td>
                    <td className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {ep.count.toLocaleString('pt-PT')}
                    </td>
                    <td className="text-sm text-slate-600 dark:text-slate-400">
                      {ep.avg_latency} ms
                    </td>
                    <td>
                      <span
                        className={`text-xs font-bold ${
                          ep.error_rate > 5
                            ? 'text-rose-600 dark:text-rose-400'
                            : 'text-emerald-600 dark:text-emerald-400'
                        }`}
                      >
                        {ep.error_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Diagnóstico & Log Explorer (X-Request-ID) */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h3 className="font-bold text-slate-900 dark:text-white text-base">
              Histórico de Chamadas Recentes & Depuração
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Copie o identificador da chamada (<code className="font-mono">X-Request-ID</code>) para pesquisar ou partilhar com o suporte técnico.
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar por ID, rota ou status..."
              className="input pl-9 text-xs"
            />
          </div>
        </div>

        {filteredRequests.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
            Nenhuma chamada recente encontrada.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Request ID</th>
                  <th>Método & Rota</th>
                  <th>Status Code</th>
                  <th>Tempo</th>
                  <th>Data & Hora</th>
                  <th className="text-right">Ação</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((r) => (
                  <tr key={r.request_id}>
                    <td>
                      <div className="flex items-center gap-1.5 font-mono text-xs text-slate-600 dark:text-slate-400">
                        <span>{r.request_id.slice(0, 13)}...</span>
                        <button
                          onClick={() => handleCopyId(r.request_id)}
                          className="p-1 text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                          title="Copiar Request ID Completo"
                        >
                          {copiedId === r.request_id ? (
                            <Check className="w-3.5 h-3.5 text-emerald-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="badge badge-info uppercase font-mono text-[10px]">
                          {r.http_method}
                        </span>
                        <span className="font-mono text-xs text-slate-900 dark:text-white font-medium">
                          {r.endpoint}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${getStatusColor(r.status_code)} text-xs font-mono font-bold`}>
                        {r.status_code}
                      </span>
                    </td>
                    <td className="text-xs text-slate-600 dark:text-slate-400">
                      {r.response_time_ms} ms
                    </td>
                    <td className="text-xs text-slate-500 dark:text-slate-400">
                      {new Date(r.created_at).toLocaleString('pt-PT')}
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => handleCopyId(r.request_id)}
                        className="btn-base btn-xs btn-secondary gap-1"
                        title="Copiar ID para suporte"
                      >
                        <Copy className="w-3 h-3" />
                        <span>Copiar ID</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
